// gift-certificate.js
//
// Renders the printable gift certificate at /pages/gift-certificate.
//
// The page is reached with display data in the query string (order reference,
// workshop name and handle, quantity) and the booking credential in the URL
// fragment: "<order id>.<gift token>". A fragment is never sent to a server in
// a request line, so the token stays out of request logs and Referer headers.
//
// The certificate always renders. Only the booking block changes: a QR code
// and link when the link verifies, and how to reach us when it does not —
// because a certificate that cannot say what it is worth is no use to whoever
// is holding it.
(function () {
  var app = document.getElementById('cert-app');
  if (!app) return;

  // Everything this page renders comes off the URL or the orders API, and it
  // is assembled with string concatenation into innerHTML. Without escaping,
  // `?order_ref=<img src=x onerror=...>` executes on the shop's own origin —
  // a reflected XSS anyone could send to a customer. Every interpolated value
  // goes through here.
  //
  // Descriptions are plain text on both paths by the time they arrive (the
  // API side runs stripHtml in workshops.ts, the storefront side takes
  // textContent below), so escaping them loses no formatting.
  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  var params = new URLSearchParams(window.location.search);
  // Shown on the certificate. Not a credential — the order number is
  // sequential, which is why it no longer opens the booking page on its own.
  var orderRef = params.get('order_ref');
  // The booking link itself: "<order id>.<gift token>", carried in the URL
  // fragment so it is never sent to a server in a request line.
  var giftLink = new URLSearchParams(window.location.hash.replace(/^#/, '')).get('t');
  var API_BASE = (typeof DARKROOM !== 'undefined' && DARKROOM.WEBHOOK_URL) || 'https://shopify-gcal-webhook-3z64u2cz5q-uc.a.run.app/api';
  var shopUrl = app.getAttribute('data-shop-url') || window.location.origin;
  var bookingUrl = giftLink ? shopUrl + '/pages/gift-booking#t=' + giftLink : null;

  // What the certificate is *for* comes from the link's own query string, so
  // the workshop name survives even when the booking link cannot be
  // verified. A certificate that cannot say what it is worth is no use to
  // whoever is holding it.
  var fallbackWorkshop = {
    title: params.get('workshop_name') || 'Darkroom workshop',
    quantity: Number(params.get('qty')) || 1,
    description: ''
  };

  // A product handle on the link lets us fetch the real description from the
  // storefront — public product data, no booking link needed. Best effort:
  // without it the certificate simply omits the blurb.
  function withDescription(workshop) {
    var handle = params.get('workshop_handle');
    if (!handle) return Promise.resolve(workshop);
    return fetch('/products/' + encodeURIComponent(handle) + '.js')
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (product) {
        if (product && product.description) {
          // description is HTML; take its text so the certificate keeps its
          // own typography and cannot be broken by stray markup.
          var tmp = document.createElement('div');
          tmp.innerHTML = product.description;
          workshop.description = (tmp.textContent || '').trim();
        }
        return workshop;
      })
      .catch(function () { return workshop; });
  }

  // Renders the certificate with the booking block replaced by how to reach
  // us: for a certificate issued before booking links were signed, and for a
  // link the service will not verify.
  function showContactMessage() {
    withDescription(fallbackWorkshop).then(function (w) {
      renderCertificate([w], false);
    });
  }

  // No token means an older certificate. Nothing to look up, so do not ask.
  if (!giftLink) {
    showContactMessage();
    return;
  }

  app.innerHTML = '<div class="cert-loading">Loading certificate&hellip;</div>';

  fetch(API_BASE + '/orders/' + encodeURIComponent(giftLink))
    .then(function(r) {
      // 403 is the uniform rejection for a link we cannot verify. Printing a
      // certificate whose QR code leads nowhere would be worse than saying so.
      if (r.status === 403 || r.status === 404) return Promise.reject('invalid');
      return r.ok ? r.json() : Promise.reject(r.status);
    })
    .then(function(data) {
      var workshops = (data.workshops && data.workshops.length)
        ? data.workshops
        : [{ title: data.title || fallbackWorkshop.title, quantity: data.quantity || fallbackWorkshop.quantity, description: data.description || '' }];
      renderCertificate(workshops, true);
    })
    .catch(function(err) {
      if (err === 'invalid') { showContactMessage(); return; }
      // A network blip should not lose the certificate: the link is good, so
      // render it bookable from what the URL already told us.
      withDescription(fallbackWorkshop).then(function (w) {
        renderCertificate([w], true);
      });
    });

  function renderCertificate(workshops, canBook) {
    // Promising a QR code we cannot draw leaves "Scan this QR code" above an
    // empty box on something meant to be printed and handed over. If the
    // library did not load, fall back to the contact message, which is
    // complete on its own.
    if (canBook && typeof QRCode === 'undefined') {
      console.error('[Certificate] qrcode.min.js did not load; showing the contact message instead');
      canBook = false;
    }

    var introText = workshops.length > 1
      ? 'This certificate entitles the recipient to attend the following workshops:'
      : 'This certificate entitles the recipient to attend:';

    var workshopsHtml = '';
    for (var i = 0; i < workshops.length; i++) {
      var w = workshops[i];
      if (i > 0) workshopsHtml += '<hr class="cert-divider">';
      workshopsHtml += '<div class="cert-workshop-block">';
      workshopsHtml += '<h2 class="cert-workshop">' + escapeHtml(w.title) + '</h2>';
      var qty = Number(w.quantity) || 1;
      workshopsHtml += '<p class="cert-attendees">' + qty + (qty === 1 ? ' attendee' : ' attendees') + '</p>';
      if (w.description) workshopsHtml += '<div class="cert-desc">' + escapeHtml(w.description) + '</div>';
      workshopsHtml += '</div>';
    }

    app.innerHTML =
      '<div class="cert-wrapper"><div class="cert">' +
      '<div class="cert-header">' +
      '<img src="https://negativedevelopment.co.uk/cdn/shop/files/Logo-Text_da88e42e-e5ea-4658-8f85-ffd0c762aff1.png?v=1666271515&width=500" class="cert-logo" alt="Negative Development" />' +
      '<div class="cert-meta"><h1>Gift Certificate</h1>' +
      (orderRef ? '<p><strong>Order reference:</strong> ' + escapeHtml(orderRef) + '</p>' : '') +
      '</div>' +
      '</div>' +
      '<p class="cert-intro">' + introText + '</p>' +
      workshopsHtml +
      (canBook
        ? '<div class="cert-qr-section"><div id="qrcode"></div>' +
          '<div class="cert-qr-text">' +
          '<p>Scan this QR code or visit the link below to choose a workshop date.</p>' +
          '<p class="cert-booking-url">' + escapeHtml(bookingUrl) + '</p>' +
          '<p class="cert-contact">Contact us: info@negativedevelopment.co.uk</p>' +
          '</div></div>'
        : '<div class="cert-qr-section"><div class="cert-qr-text">' +
          '<p><strong>This certificate is valid and has not expired.</strong></p>' +
          '<p>To choose a date, email us at info@negativedevelopment.co.uk' +
          (orderRef ? ', quoting order ' + escapeHtml(orderRef) : '') +
          ', and we will book the session for you.</p>' +
          '</div></div>') +
      '</div></div>' +
      '<div class="cert-actions no-print"><button onclick="window.print()" class="cert-print-btn">Download as PDF / Print</button></div>';

    if (canBook) {
      new QRCode(document.getElementById('qrcode'), {
        text: bookingUrl,
        width: 140,
        height: 140,
        correctLevel: QRCode.CorrectLevel.M
      });
    }
  }
  })();
